import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DataFhirAdapter,
  DataFhirCategorySetting,
  DataFhirModel,
} from '../data.model';
import { DictionariesCollectionService } from '../dictionaries/dictionaries-collection.service';
import { BasePersonAdapter, Human } from './base-person.model';

export interface Practitioner extends DataFhirModel<fhir4.Practitioner>, Human {
  identifier: string;
  practitionerType: string;
  specialty: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PractitionerAdapter extends DataFhirAdapter<Practitioner> {
  categorySettings: DataFhirCategorySetting<Practitioner>[] = [
    {
      categoryName: 'practitionerType',
      dictionaryIds: ['1.2.276.0.76.11.31--20211015121326'],
      resourcePosition: (fhirPractitioner) =>
        fhirPractitioner?.extension
          .filter(
            ({ url }) =>
              url ==
              'http://icw-global.com/fhir/StructureDefinition/hpd-practitioner-type', //NOSONAR
          )
          .map(({ valueCodeableConcept }) => valueCodeableConcept),
    },
    {
      categoryName: 'specialty',
      dictionaryIds: ['1.2.276.0.76.11.37--20190517134631'],
      resourcePosition: (fhirPractitioner) =>
        fhirPractitioner?.extension
          .filter(
            ({ url }) =>
              url ==
              'http://icw-global.com/fhir/StructureDefinition/hpd-practitioner-specialty', //NOSONAR
          )
          .map(({ valueCodeableConcept }) => valueCodeableConcept),
    },

    {
      categoryName: 'practiceSettings',
      dictionaryIds: ['1.2.276.0.76.11.69--20211114160337'],
      resourcePosition: (fhirPractitioner) =>
        fhirPractitioner?.extension
          .filter(
            ({ url }) =>
              url ==
              'http://icw-global.com/fhir/StructureDefinition/hpd-practitioner-setting', //NOSONAR
          )
          .map(({ valueCodeableConcept }) => valueCodeableConcept),
    },
  ];

  constructor(
    protected dictionariesCollection: DictionariesCollectionService,
    private humanAdapter: BasePersonAdapter,
  ) {
    super('Practitioner', dictionariesCollection);
  }

  fromResource$(
    fhirPractitioner: fhir4.Practitioner,
  ): Observable<Practitioner> {
    return forkJoin([
      super.fromResource$(fhirPractitioner),
      this.humanAdapter.fromResource$(fhirPractitioner),
    ]).pipe(
      map(([baseItem, human]) => {
        const hcpid = fhirPractitioner.identifier.find(
          (x) => x.type?.coding.some(({ code }) => code === 'HCPID'),
        );
        const identifier = hcpid
          ? `${hcpid?.system?.split(':').pop()}@${hcpid?.value}`
          : '';

        const practitionerType =
          baseItem?._categories?.['practitionerType']?.map(
            ({ name }) => name,
          )?.[0] || '';
        const specialty = baseItem?._categories?.['specialty']?.map(
          ({ name }) => name,
        );

        return {
          ...baseItem,
          ...human,
          identifier,
          practitionerType,
          specialty,
        };
      }),
    );
  }

  protected blankResource(): fhir4.Practitioner {
    return null;
  }

  toResource(practitioner: Practitioner, options): fhir4.Practitioner {
    return null;
  }
}
